import React, { memo, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, FormControlLabel, Switch, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import classes from "./OrderSearchPanel.module.scss";
import MultiSelectChipGroup from "./MultiSelectChipGroup";
import DepartmentMultiSelect from "./DepartmentMultiSelect";
import DateRangeWithPresets from "./DateRangeWithPresets";
import {
  DATE_KIND_OPTIONS,
  STATUS_OPTIONS,
  getFieldLocks,
} from "../../../functions/order/orderSearchModel";
import { getDatePresets } from "../../../functions/order/orderSearchPresets";
import { fetchdepartments } from "../../../reducks/department/operations";
import { getDepartments } from "../../../reducks/department/selectors";
import { getUserInfo } from "../../../reducks/users/selectors";

// 発注処理・発注履歴で共通の検索条件パネル。
// (従来は FilterComponents.jsx / FilterComponentsForOrderProcessing.jsx として
//  ほぼ同一の実装が2枚あったものを一本化した)
//
// 条件の実体は useOrderSearchConditions が管理し、ここは表示と入力だけを担う。
// 権限による固定(部署・このアカウントのみ)は getFieldLocks に従って disabled 表示するが、
// 値の強制そのものは normalizeConditions 側で常時かかるため、
// この UI の disabled はあくまで案内であって防御ではない。
// 日付種類の絞り込みルール(いわゆる OR 条件)のユーザー向け説明。
// ラベル右の「?」ホバーで表示する(初見では複数選択時の挙動が分かりにくいため)
const DATE_KIND_HELP = [
  "複数選んだときは、選んだ日付のどれかが期間内に入っている注文をすべて表示します。",
  "例: 「注文日」と「発注日」を選ぶと、どちらか一方でも期間内なら表示されます。",
  "その日付がまだ入っていない注文は対象外です(例: 「取消日」だけを選ぶと、取消されていない注文は表示されません)。",
].join("\n");

const OrderSearchPanel = memo((props) => {
  const {
    screen,
    conditions,
    onChange,
    onReset,
    isLoading = false,
    error = null,
    resultCount = 0,
  } = props;
  const dispatch = useDispatch();
  const { role } = useSelector(getUserInfo);
  const departmentsOnFirestore = useSelector(getDepartments);

  useEffect(() => {
    dispatch(fetchdepartments());
  }, [dispatch]);

  // 検索条件側は部署IDを文字列で持つため、マスタ側も文字列に寄せる。
  // 並びは拠点→部署ID順にそろえる: Autocomplete の groupBy は「同じグループが
  // 連続している」前提で、拠点をまたいで did が飛んでいると同じ拠点見出しが
  // 複数回表示されてしまうため(マスタは did 昇順で届くが、拠点単位の連続性までは
  // データ側で保証されていない)
  const departments = useMemo(
    () =>
      (departmentsOnFirestore || [])
        .map((department) => ({
          id: String(department.did),
          name: department.name,
          baseId: String(department.base_id),
          baseName: department.base_name,
        }))
        .sort(
          (a, b) =>
            a.baseId.localeCompare(b.baseId, "ja", { numeric: true }) ||
            a.id.localeCompare(b.id, "ja", { numeric: true })
        ),
    [departmentsOnFirestore]
  );

  const locks = getFieldLocks(screen, role);
  const presets = useMemo(() => getDatePresets(), []);

  // 条件に部署マスタに無いIDが含まれる場合の注意表示。
  // 例: 発注担当者の自部署がマスタから削除されると、選択欄は空に見えるのに
  // 絞り込みだけが効いて0件になり、しかも disabled で本人には直せない。
  // その状態を黙って見せない(マスタ未ロード中は判定しない)
  const hasUnknownDepartment =
    departments.length > 0 &&
    Array.isArray(conditions.departmentIds) &&
    conditions.departmentIds.some(
      (id) => !departments.some((department) => department.id === id)
    );

  return (
    <section className={classes.root}>
      <div className={classes.row}>
        <MultiSelectChipGroup
          label="日付種類"
          help={DATE_KIND_HELP}
          options={DATE_KIND_OPTIONS}
          value={conditions.dateKinds}
          onChange={(dateKinds) => onChange({ dateKinds })}
        />
        <DateRangeWithPresets
          from={conditions.from}
          to={conditions.to}
          presets={presets}
          onChange={({ from, to }) => onChange({ from, to })}
        />
      </div>
      <div className={classes.row}>
        <MultiSelectChipGroup
          label="状態"
          options={STATUS_OPTIONS}
          value={conditions.statuses}
          onChange={(statuses) => onChange({ statuses })}
        />
        <DepartmentMultiSelect
          departments={departments}
          value={conditions.departmentIds}
          onChange={(departmentIds) => onChange({ departmentIds })}
          disabled={locks.department}
        />
        <FormControlLabel
          className={classes.myOwn}
          labelPlacement="start"
          disabled={locks.myOwn}
          label={
            <Typography className={classes.myOwnLabel}>
              このアカウントのみ
            </Typography>
          }
          control={
            <Switch
              size="small"
              checked={conditions.myOwn}
              onChange={(event) => onChange({ myOwn: event.target.checked })}
            />
          }
        />
        <Button
          className={classes.reset}
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={onReset}
        >
          初期値に戻す
        </Button>
        <span
          className={`${classes.summary} ${error ? classes.error : ""}`}
          role="status"
        >
          {error
            ? "取得に失敗しました。通信環境を確認してください"
            : isLoading
            ? "検索中…"
            : `該当 ${Number(resultCount).toLocaleString()} 件`}
        </span>
      </div>
      {hasUnknownDepartment && (
        <div className={classes.warning}>
          部署マスタに存在しない部署が検索条件に含まれています(該当分は表示されません)。「初期値に戻す」で解消できます。
        </div>
      )}
    </section>
  );
});

export default OrderSearchPanel;
